import {
  OwnershipModelHelper,
  type OwnershipModel,
  type Relation,
} from '../model';
import { BlockLayoutHelper, type BlockContext } from './block-layout-helper';
import { CycleHelper } from './cycle-helper';
import { ForestHelper, type Forest } from './forest-helper';
import { LabelPlacementHelper, type LabelText } from './label-placement-helper';
import { LayoutBoundsHelper } from './layout-bounds-helper';
import {
  LayoutGeometryHelper as G,
  type NodeFrame,
} from './layout-geometry-helper';
import { LoopChannelHelper } from './loop-channel-helper';
import { MultiOwnerHelper } from './multi-owner-helper';
import { ReferenceChipHelper } from './reference-chip-helper';
import { SharedBandHelper } from './shared-band-helper';
import type { LayoutDiagnostic, LayoutResult } from './structure-layout.types';
import type { TextMeasurer } from './text-measure-helper';
import { TrayHelper } from './tray-helper';

class StructureLayoutHelper {
  static readonly TRAY_GAP = 64;

  static compute(
    model: Readonly<OwnershipModel>,
    measurer: Readonly<TextMeasurer>,
    frame: Readonly<NodeFrame> = G.CARD_FRAME
  ): LayoutResult {
    const loopIds = CycleHelper.findLoopRelationIds(
      model.entities.map((entity) => entity.id),
      model.relations
    );
    return StructureLayoutHelper.computeWith(
      model,
      measurer,
      frame,
      loopIds,
      []
    );
  }

  // A loop with no clear channel becomes a pair of chips, and the chart is laid out again around them.
  private static computeWith(
    model: Readonly<OwnershipModel>,
    measurer: Readonly<TextMeasurer>,
    frame: Readonly<NodeFrame>,
    loopIds: ReadonlySet<string>,
    chippedLoops: ReadonlyArray<Relation>
  ): LayoutResult {
    const labelOf = StructureLayoutHelper.createLabelLookup(measurer);
    const { forest, shared, chipped } = MultiOwnerHelper.resolve(
      model,
      loopIds,
      chippedLoops
    );
    const chipsOf = ReferenceChipHelper.createDrafts(
      chipped,
      model,
      loopIds,
      measurer
    );
    const gutters = SharedBandHelper.getGutters(shared, labelOf);
    const sharedByHost = new Map(
      shared.map((placement) => [
        MultiOwnerHelper.getHostKey(placement.hostId, placement.rowIndex),
        placement,
      ])
    );
    const ctx: BlockContext = {
      forest,
      frame,
      indent: StructureLayoutHelper.getIndent(forest, labelOf),
      labelOf,
      chipsOf: (entityId) => chipsOf.get(entityId) ?? [],
      gutterOf: (entityId) => gutters.get(entityId) ?? 0,
      sharedOf: (hostId, rowIndex) =>
        sharedByHost.get(MultiOwnerHelper.getHostKey(hostId, rowIndex)),
    };
    const forestBlock = BlockLayoutHelper.layoutForest(forest.roots, ctx);

    const chippedIds = new Set(chippedLoops.map((relation) => relation.id));
    const routing = LoopChannelHelper.route(
      forest.loopRelations.filter((relation) => !chippedIds.has(relation.id)),
      {
        nodes: forestBlock.nodes,
        lines: forestBlock.lines,
        annotations: [...forestBlock.labels, ...forestBlock.chips],
      },
      (relation) =>
        LabelPlacementHelper.getLabelText(
          StructureLayoutHelper.getLoopText(relation),
          measurer
        ),
      frame
    );
    if (routing.blocked.length > 0)
      return StructureLayoutHelper.computeWith(
        model,
        measurer,
        frame,
        loopIds,
        [...chippedLoops, ...routing.blocked]
      );

    const trayOrigin = {
      x: 0,
      y:
        forest.roots.length > 0
          ? forestBlock.height + StructureLayoutHelper.TRAY_GAP
          : 0,
    };
    const tray = TrayHelper.layout(forest.trayIds, trayOrigin, measurer, frame);
    const nodes = [...forestBlock.nodes, ...(tray?.nodes ?? [])];
    const lines = [...forestBlock.lines, ...routing.lines].map((line) => ({
      ...line,
      id: `line:${line.relationId}`,
      d: G.getPathD(line.points),
    }));
    const labels = [...forestBlock.labels, ...routing.labels];
    const diagnostics: LayoutDiagnostic[] = [
      ...model.diagnostics,
      ...forest.loopRelations.map(
        (relation): LayoutDiagnostic => ({
          code: 'loop',
          entityId: relation.ownedId,
          relationId: relation.id,
        })
      ),
      ...chippedLoops.map(
        (relation): LayoutDiagnostic => ({
          code: 'route-blocked',
          entityId: relation.ownedId,
          relationId: relation.id,
        })
      ),
    ];
    return {
      nodes,
      lines,
      labels,
      chips: forestBlock.chips,
      tray: tray?.tray ?? null,
      bounds: LayoutBoundsHelper.compute({
        nodes,
        lines,
        labels,
        chips: forestBlock.chips,
        tray: tray?.tray ?? null,
      }),
      diagnostics,
    };
  }

  static getIndent(
    forest: Readonly<Forest>,
    labelOf: (relation: Readonly<Relation>) => LabelText | null
  ): number {
    const widths = [...forest.primaryRelationOf.values()]
      .filter(
        (relation) =>
          ForestHelper.isLeaf(forest, relation.ownedId) &&
          ForestHelper.getChildren(forest, relation.ownerId).includes(
            relation.ownedId
          )
      )
      .map((relation) => labelOf(relation)?.width ?? 0);
    return LabelPlacementHelper.getIndent(Math.max(0, ...widths));
  }

  static createLabelLookup(
    measurer: Readonly<TextMeasurer>
  ): (relation: Readonly<Relation>) => LabelText | null {
    const cache = new Map<string, LabelText | null>();
    return (relation) => {
      const cached = cache.get(relation.id);
      if (cached !== undefined) return cached;
      const text = OwnershipModelHelper.getShareLabel(relation);
      const label = text
        ? LabelPlacementHelper.getLabelText(text, measurer)
        : null;
      cache.set(relation.id, label);
      return label;
    };
  }

  private static getLoopText(relation: Readonly<Relation>): string {
    const share = OwnershipModelHelper.getShareLabel(relation);
    return share ? `${share} · loop` : 'loop';
  }
}

export { StructureLayoutHelper };
